import DataAssetLoader from '@sparta.global.utilities/data-asset-loader-utility/dist/es6.js';
import Handlebars from '@sparta.global.vendors/handlebars';
import spartaGlobalFunctions from '@sparta.global.utilities/sparta-global-functions-utility/dist/es6.js';
import spartaEventsUtility from '@sparta.global.utilities/sparta-events-utility/dist/es6.js';
import spartaStoreUtility from '@sparta.global.utilities/sparta-store-utility/dist/es6.js';
import { cinemaThumbnailHelper } from '@sparta.global.helpers/cinema-helper/dist/es6.js';

/**
 * Namespace for the utility.
 * @constant {string}
 */
const utilityNamespace = 'SpartaCinemaUtility';
let toggleTranscriptTimeout;

/**
* Collection of namespaced event strings.
* @constant {object}
*/
const EVENTS = {
  Cinema_Ready: `${utilityNamespace}.Cinema_Ready`
};

/**
 * Looks up the Kaltura account and player ID from numerous sources
 * @param {String} paramLookup Key to look up
 * @param {Object} globalParams Page/Site params
 * @returns {String} account or player ID for Kaltura
 */
function getPlayerParam(paramLookup, globalParams) {
  // Boolean setters on if param is in page or site params
  const fromPageParams = globalParams && 'page' in globalParams && paramLookup in globalParams.page;
  const fromSiteParams = globalParams && 'site' in globalParams && paramLookup in globalParams.site;

  // Look for param in page config
  let pageLookup;
  pageLookup = fromPageParams ? globalParams.page[paramLookup] : globalParams.pageParams ? globalParams.pageParams[paramLookup] : pageLookup;

  // look for param in site config
  let siteLookup;
  siteLookup = fromSiteParams ? globalParams.site[paramLookup] : globalParams.siteParams ? globalParams.siteParams[paramLookup] : pageLookup;
  return pageLookup || siteLookup;
}

/**
 * Returns an object with the merged options for the utility to run
 * @param {Object} settings Object from utility init call
 * @returns {Object} Settings constructor
 */
function setOptions(settings) {
  const language = spartaGlobalFunctions.getLanguage();
  const spaParams = spartaStoreUtility.getData('spaParams');

  // If `options` is a string, convert it to object and place its value as the container ID
  if (typeof settings === 'string') {
    settings = {
      containerId: settings
    };
  }

  // Settings constructor
  return new function optionsConstructor() {
    // Kill check
    this.containerId = settings.containerId;
    if (!this.containerId) {
      return this;
    }

    // Check if provided ID already includes the namespace prefix
    if (this.containerId.includes('spaCinema_')) {
      this.containerId = this.containerId.replace('spaCinema_', '');
    }

    // Get elements
    this.globalStrings = spaParams.global[language].video;
    this.container = document.getElementById(`spaCinema_${this.containerId}`);
    this.playerElm = this.container.querySelector(`[id="spaCinema_${this.containerId}_player"]`);
    this.playerStatus = this.container.querySelector('.spa-cinema-status');
    this.playerTitle = this.container.querySelector('.spa-cinema-title');
    this.playerDesc = this.container.querySelector('.spa-cinema-description');
    this.playerTranscript = this.container.querySelector('.spa-cinema-video-transcript');
    this.playerTranscriptTrigger = this.container.querySelector('.spa-cinema-video-transcript-trigger');
    this.playerTranscriptContent = this.container.querySelector('.spa-cinema-video-transcript-content');
    this.attributeValues = {
      stage: 'spa-cinema-stage'
    };

    // Parse options data attribute
    const getOptions = JSON.parse(this.container.getAttribute('data-options'));
    this.options = getOptions;
    this.autoEmbed = getOptions.autoEmbed.toString() === 'true';
    this.autoStage = getOptions.autoStage.toString() === 'true';
    this.accountId = getOptions.accountId;
    this.playerID = getOptions.playerID;
    this.environmentHost = getOptions.environmentHost;
    this.videoId = getOptions.videoId;
    this.playbackSettings = Object.assign({
      autoplay: false,
      muted: false,
      allowMutedAutoPlay: true,
      pictureInPicture: false,
      thumbnailUrl: settings.videoThumbnailSrc
    }, getOptions.playbackSettings);

    // Video thumbnails
    this.mediaInfo = {};
    this.hasPoster = getOptions.hasPoster;
    this.videoThumbnails = {};
    this.videoThumbnailSizes = [1296, 1140, 1025, 768, 480];
    this.imageUrlDefault = `https://cfvod.kaltura.com/p/${this.accountId}/sp/${this.accountId}00/thumbnail/entry_id/${this.videoId}/version/100001`;

    // This segment is unique to V6 in how default values are pulled
    this.accountId = getPlayerParam('kalturaAccountId', spaParams) || getOptions.accountId;
    this.playerID = getPlayerParam('kalturaPlayerId', spaParams) || getOptions.playerID;
    this.environmentHost = getPlayerParam('kalturaCDN', spaParams) || getOptions.environmentHost;
  }();
}

/**
 * Retrieves the video JSON using the Kaltura API
 * @param {Object} settings Object of utility settings
 * @param {String} entryId ID of the video to look up
 * @returns 
 */
function getVideoData(settings) {
  // Check if poster was added through JCR/helper
  const hasPoster = settings.hasPoster;
  if (!hasPoster) {
    // If not provided, attempt to get it from Kaltura CDN
    // Build thumbnail sizes and srcset attributes
    settings.videoThumbnailSizes.forEach(size => {
      settings.videoThumbnails[size] = `${settings.imageUrlDefault}/width/${size}/type/1/quality/X`;
    });

    // Add thumbnail ++ manual play button
    settings.playerElm.insertAdjacentHTML('beforeend', createPreviewImage(settings));
  }

  // Update settings
  settings.videoThumbnail = settings.playerElm.querySelector('.spa-cinema-thumbnail--large');
  if (settings.videoThumbnail) {
    settings.videoThumbnailSrcset = settings.videoThumbnail.getAttribute('srcset');
    settings.videoThumbnailSrc = settings.videoThumbnail.getAttribute('src');
  }
  settings.playerTrigger = settings.playerElm.querySelector('.spa-cinema-thumbnail-link');
  settings.videoName = settings.options.title || '';
  settings.videoDescription = settings.options.description || '';

  // Now that image has been created (from JCR or CDN) add to mediaInfo object
  // The mediaInfo object passes extra options into the Kaltura player
  // The only time poster should get overwritten is if JCR defines it
  if (hasPoster) {
    settings.mediaInfo.poster = settings.videoThumbnailSrc;
  }
  return settings;
}

/**
 * Create a video thumbnail and append it to the DOM
 * @param {Object} settings Object of utility settings
 */
function createPreviewImage(settings) {
  const {
    containerId,
    options,
    globalStrings,
    autoEmbed
  } = settings;
  const videoImageObj = settings.videoThumbnails;
  if (videoImageObj) {
    // Build JCR for thumbnail helper
    const videoThumbnailJcr = {
      containerId,
      options,
      autoEmbed,
      poster: `${Object.keys(videoImageObj).map(key => `${videoImageObj[key]} ${key}w`).join(', ')}`,
      globalStrings: globalStrings
    };

    // Get thumbnail HTML string
    return cinemaThumbnailHelper(videoThumbnailJcr);
  }
  return '';
}

/**
 * Retrieves the spa-assets URL for the utility's iframe folder which includes a CSS file.
 * @param {Object} folderName Name of folder in module root to pull file from
 * @param {Object} fileName Name of file
 * @returns 
 */
function getAssetFileUrl(folderName, fileName) {
  // generate a new DataAssetLoader instance
  const getAssetLoader = new DataAssetLoader('@sparta.global.utilities/cinema-utility', folderName);

  // Get file URL
  let assetUrl = getAssetLoader.buildAssetUrl(fileName);
  return assetUrl;
}

/**
 * Readies the player for an iFrame injection embed
 * @param {Object} settings Object for utility options
 * @param {Object} autoPlayVideo Auto play the video on creation
 * @returns iFrame HTML markup string
 */
function createKalturaPlayerIframe(settings, autoPlayVideo) {
  const stylesUrl = getAssetFileUrl('plugins', 'player-overrides.css');

  // Build the poster thumbnail
  let videoThumbnailSrc;
  if (settings.hasPoster && settings.videoThumbnailSrc && spaParams) {
    const domain = spaParams.siteParams.domain;
    let {
      hostname
    } = domain;
    hostname = hostname === window.location.host ? hostname : window.location.host;
    const {
      ssl
    } = domain;
    const getProtocol = ssl ? 'https' : 'http';

    // Set valid image URL
    videoThumbnailSrc = `${getProtocol}://${hostname}${settings.videoThumbnailSrc.replace('./spa-assets/', 'spa-assets/')}`;
  }

  // Update auto play setting
  settings.playbackSettings.autoplay = autoPlayVideo ? autoPlayVideo : settings.playbackSettings.autoplay;

  // Base config object
  const config = {
    targetId: settings.playerElm ? settings.playerElm.id : settings.id,
    provider: {
      partnerId: settings.accountId,
      uiConfId: settings.playerID,
      entryId: settings.videoId
    },
    playback: settings.playbackSettings,
    ui: {
      css: stylesUrl
    },
    sources: {
      ...(videoThumbnailSrc && {
        poster: videoThumbnailSrc
      })
    },
    plugins: {
      'playkit-js-transcript': {
        expandOnFirstPlay: false
      }
    }
  };

  // Stringify config segments for src attribute appending as Kaltura expects it
  const playBack = `${Handlebars.Utils.escapeExpression(JSON.stringify(config.playback))}`;
  const ui = `${Handlebars.Utils.escapeExpression(JSON.stringify(config.ui))}`;
  const sources = `${Handlebars.Utils.escapeExpression(JSON.stringify(config.sources))}`;
  const plugins = `${Handlebars.Utils.escapeExpression(JSON.stringify(config.plugins))}`;

  // HTML to get appended to page
  return `
    <iframe
      id="${settings.containerId}IframeContainer"
      type="text/javascript"
      src='https://cdnapisec.kaltura.com/p/${config.provider.partnerId}/embedPlaykitJs/uiconf_id/${config.provider.uiConfId}?iframeembed=true&entry_id=${config.provider.entryId}&config[playback]=${playBack}&config[ui]=${ui}&config[sources]=${sources}&config[plugins]=${plugins}'
      class="sparta-kaltura-player-container"
      allowfullscreen
      webkitallowfullscreen
      mozAllowFullScreen
      frameborder="0"
      allow="autoplay; fullscreen"
      loading="lazy"
    >
    </iframe>
  `;
}

/**
 * Adds the initial click event to toggle the transcript
 * @param {Object} settings Object for utility options
 */
function setupTranscriptToggler(settings) {
  const {
    playerTranscript,
    playerTranscriptTrigger
  } = settings;
  if (playerTranscript && playerTranscriptTrigger) {
    playerTranscript.classList.add('spa-cinema-video-transcript--hidden');
    playerTranscriptTrigger.addEventListener('click', () => {
      toggleTranscript(settings);
    });
  }
}

/**
 * Gets height of hidden element to smooth scroll
 * @param {Object} el Dom element to clone and get height from
 * @returns 
 */
function getHiddenHeight(el) {
  const clone = el.cloneNode(true);
  const buffer = 16;
  Object.assign(clone.style, {
    overflow: 'visible',
    height: 'auto',
    maxHeight: 'none',
    opacity: '0',
    visibility: 'hidden',
    display: 'block'
  });
  el.after(clone);
  const height = clone.offsetHeight;
  clone.remove();
  return height + buffer;
}

/**
 * Toggles the transcript
 * Includes any ADA schema attached to the button
 * @param {Object} settings Object for utility options
 */
function toggleTranscript(settings) {
  const {
    playerTranscript,
    playerTranscriptContent,
    playerTranscriptTrigger,
    state
  } = settings;
  const classNamespace = 'spa-cinema-video-transcript';
  const activeClass = `${classNamespace}--active`;
  const hiddenClass = `${classNamespace}--hidden`;

  // Transition timing from CSS
  const buffer = 100;
  const transitionTimer = Number((window.getComputedStyle(playerTranscriptContent).getPropertyValue('transition-duration') ? window.getComputedStyle(playerTranscriptContent).getPropertyValue('transition-duration').replace('s', '') : 300) * 1000);

  // Get height of content
  const containerH = `${getHiddenHeight(playerTranscriptContent)}px`;

  // Clear timeout
  window.clearTimeout(toggleTranscriptTimeout);

  // Show layer
  if (playerTranscript.classList.contains(hiddenClass)) {
    playerTranscript.classList.remove(hiddenClass);
    playerTranscript.classList.add(activeClass);
    playerTranscriptTrigger.setAttribute('aria-expanded', 'true');
    setTimeout(() => {
      playerTranscriptContent.style.height = containerH;
    }, 0);
    toggleTranscriptTimeout = window.setTimeout(() => {
      playerTranscriptContent.style.height = 'auto';
    }, transitionTimer + buffer);
  }
  // Hide layer
  else {
    playerTranscriptContent.style.height = containerH;
    playerTranscript.classList.remove(activeClass);
    playerTranscriptTrigger.setAttribute('aria-expanded', 'false');
    setTimeout(() => {
      playerTranscriptContent.style.height = '0px';
    }, 0);
    toggleTranscriptTimeout = window.setTimeout(() => {
      playerTranscript.classList.add(hiddenClass);
      playerTranscriptContent.style.height = '';
    }, transitionTimer + buffer);
  }
}

/**
 * Initializes a new instance of the class.
 * Moved to seperate function to help support widget constructor usage with bootstrap events
 * @param {string || object} options - DOM element(s) to create utility events from.
 * @returns {void}
 */
function initCinemaPlayer(options = {}) {
  // Merge default with custom settings
  const settings = setOptions(options);

  // Kill check
  if (settings.container) {
    // Try to create video from vendor CDN
    getVideoData(settings);

    // Embed video
    if (settings.autoEmbed) {
      // Add stage class
      settings.playerElm.classList.add(settings.attributeValues.stage);

      // Embed video
      const iframeHtml = createKalturaPlayerIframe(settings);
      settings.playerElm.insertAdjacentHTML('beforeend', iframeHtml);
    }
    // If not embeding the video, enable manual embed
    else if (settings.playerTrigger) {
      // Add stage class
      if (settings.autoStage) {
        settings.playerElm.classList.add(settings.attributeValues.stage);
      }

      // Embed on click
      settings.playerTrigger.addEventListener('click', () => {
        // Remove trigger
        settings.playerTrigger.remove();

        // Embed player
        const iframeHtml = createKalturaPlayerIframe(settings, true);
        settings.playerElm.insertAdjacentHTML('beforeend', iframeHtml);
      });
    }

    // Set up transcript toggle
    setupTranscriptToggler(settings);

    // Update element that instance has been created
    settings.container.setAttribute('data-player-initalized', true);

    // Publish event that player is built
    spartaEventsUtility.pub(`${EVENTS.Cinema_Ready}:${settings.containerId}`, settings);
  } else {
    return '';
  }
}

/**
 * Configures inputs for validation, formatting, obfuscation, etc.
 */
class SpartaCinemaUtility {
  /**
   * Enable widget support for component
   */
  constructor() {
    // this will get the widget name/version from the script tag. if it is loaded in a widget
    let widgetName, widgetVersion;
    this.widgetInfo = {};
    try {
      if (document.currentScript) {
        widgetName = document.currentScript.getAttribute('widget-name');
        widgetVersion = document.currentScript.getAttribute('widget-version');
        this.widgetInfo = {
          id: widgetName,
          version: widgetVersion
        };
      }
    } catch (e) {
      console.error('Failed to process currentScript attributes');
    }
  }

  /**
  * Initializes a new instance of the class.
  * @param {string || object} options - DOM element(s) to create utility events from.
  * @returns {void}
  */
  init(options = {}) {
    // Add base listeners for opening via click methods.
    spartaEventsUtility.sub(`SPARTA_GLOBAL_READY.spartaBootstrapUtility`, initCinemaPlayer(options), true, this.widgetInfo);
  }

  /**
   * Removes the Kaltura video iframe DOM element
   * @param {string} containerId Id of the cinema instance wrapper
   * @returns 
   */
  destroyVideo(containerId) {
    if (containerId && document.getElementById(containerId)) {
      const playerElm = document.getElementById(containerId);
      const playerIframe = playerElm.querySelector('.sparta-kaltura-player-container');

      // Kill check
      if (!playerIframe) {
        console.warn('Video iFrame not provided or not created');
        return '';
      }

      // Remove iframe from DOM
      playerIframe.remove();
    } else {
      console.warn('Video ID not provided or not found in DOM for destroy method', containerId);
      return '';
    }
  }

  /**
   * Creates the Kaltura video iframe DOM element into an already initalized player
   * @param {string} containerId Id of the cinema instance wrapper
   * @returns 
   */
  createVideo(containerId, playbackSettings) {
    if (containerId && document.getElementById(containerId)) {
      const container = document.getElementById(containerId);
      const hasiFrame = container.querySelector('iframe');

      // Kill check
      if (!container.getAttribute('data-player-initalized') || hasiFrame) {
        console.warn('Video instance has not been initalized or already has a video iFrame.');
        return '';
      }

      // Get a paired down version of options from wrapper
      const options = JSON.parse(container.getAttribute('data-options'));
      options.id = `${containerId}_player`;
      options.playbackSettings = (options.playbackSettings, playbackSettings);

      // Render new HTML
      const playerElm = container.querySelector(`[id="${options.id}"]`);
      if (playerElm) {
        const iframeHtml = createKalturaPlayerIframe(options);
        playerElm.insertAdjacentHTML('beforeend', iframeHtml);
      }
    } else {
      console.warn('Video ID not provided or not found in DOM for create method', containerId);
      return '';
    }
  }
}

/**
 * Instance of the Cinema utility, which can be used to create players for specific videos.
 * @constant {object}
 */
const spartaCinemaUtility = new SpartaCinemaUtility();

export { spartaCinemaUtility as default };
